<script setup>
import GoalsPanel from '@/components/goals/GoalsPanel.vue'
import MilestonesPanel from '@/components/milestones/MilestonesPanel.vue'
import NotesPanel from '@/components/notes/NotesPanel.vue'
import ObservationsPanel from '@/components/observations/ObservationsPanel.vue'
import PlansPanel from '@/components/plans/PlansPanel.vue'
import ProgressPanel from '@/components/progress/ProgressPanel.vue'
import TimelinePanel from '@/components/timeline/TimelinePanel.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import StatusChip from '@/components/shared/StatusChip.vue'
import { formatCurrency, formatDate } from '@core/utils/formatters'
import { useClientsStore } from '@/stores/clients'
import { useCoachingPlansStore } from '@/stores/coachingPlans'
import { useGoalsStore } from '@/stores/goals'
import { usePricingPlansStore } from '@/stores/pricingPlans'
import {
  billingCadenceLabel,
  useSubscriptionsStore,
} from '@/stores/subscriptions'
import { useSessionPackagesStore } from '@/stores/sessionPackages'
import {
  paymentMethodLabel,
  usePaymentsStore,
} from '@/stores/payments'
import {
  readStoredClientDetailSection,
  resolveClientDetailSection,
  writeStoredClientDetailSection,
} from '@/utils/clientDetailSection'

definePage({
  meta: {
    navActiveLink: 'clients',
  },
})

const route = useRoute()
const router = useRouter()
const store = useClientsStore()
const billingStore = useSubscriptionsStore()
const packagesStore = useSessionPackagesStore()
const pricingPlansStore = usePricingPlansStore()
const paymentsStore = usePaymentsStore()
const goalsStore = useGoalsStore()
const plansStore = useCoachingPlansStore()
const { t } = useI18n()
const formError = ref('')
const formSuccess = ref('')
const reviewerNote = ref('')

// F-039: Coaching / Billing / Profile groupings. Inactive VWindowItems stay
// mounted (`eager`) so in-progress forms are preserved across tab switches.
const activeSection = computed({
  get: () => resolveClientDetailSection({
    query: route.query.section,
    stored: readStoredClientDetailSection(),
  }),
  set: section => {
    const next = resolveClientDetailSection({ query: section })

    writeStoredClientDetailSection(next)
    if (route.query.section === next)
      return

    router.replace({
      query: {
        ...route.query,
        section: next,
      },
    })
  },
})

const startDialog = ref(false)
const sellPackageDialog = ref(false)
const selectedPlanId = ref(null)
const selectedPackagePlanId = ref(null)
const paymentMethod = ref('cash')
const packagePendingCancelId = ref(null)
const pendingPaymentId = ref(null)
const partialDialog = ref(false)
const partialInvoice = ref(null)
const partialAmountEuros = ref('')
const partialPaymentMethod = ref('cash')

const confirmDialog = ref({
  open: false,
  action: null,
  title: '',
  body: '',
  confirmLabel: '',
  color: 'primary',
  requireAcknowledgment: false,
  acknowledgmentLabel: '',
})

const form = ref({
  name: '',
  email: '',
  date_of_birth: '',
  gender: '',
  phone: '',
  address_line1: '',
  address_line2: '',
  city: '',
  postal_code: '',
  country: '',
  medical_info: '',
  allergies: '',
  injuries: '',
  health_history: '',
})

const relationshipState = computed(() => store.current?.coaching_relationship?.lifecycle_state ?? null)
const relationshipId = computed(() => store.current?.coaching_relationship?.id ?? null)
const relationshipEnded = computed(() => relationshipState.value === 'ended')
const openSubscription = computed(() => billingStore.openSubscription)

const publishedSubscriptionPlans = computed(() =>
  (pricingPlansStore.pricingPlans || []).filter(
    p => p.lifecycle_state === 'published' && p.plan_type === 'subscription',
  ),
)

const publishedPackagePlans = computed(() =>
  (pricingPlansStore.pricingPlans || []).filter(
    p => p.lifecycle_state === 'published' && p.plan_type === 'session_package',
  ),
)

const selectedPlan = computed(() =>
  publishedSubscriptionPlans.value.find(p => p.id === selectedPlanId.value) || null,
)

const selectedPackagePlan = computed(() =>
  publishedPackagePlans.value.find(p => p.id === selectedPackagePlanId.value) || null,
)

const invoiceHeaders = computed(() => [
  { title: t('subscriptions.invoiceColumns.description'), key: 'description' },
  { title: t('subscriptions.invoiceColumns.amount'), key: 'amount' },
  { title: t('subscriptions.invoiceColumns.dueOn'), key: 'due_on' },
  { title: t('subscriptions.invoiceColumns.status'), key: 'lifecycle_state' },
  { title: t('subscriptions.invoiceColumns.actions'), key: 'actions', sortable: false },
])

const paymentHeaders = computed(() => [
  { title: t('payments.columns.date'), key: 'date' },
  { title: t('payments.columns.amount'), key: 'amount' },
  { title: t('payments.columns.method'), key: 'payment_method' },
  { title: t('payments.columns.status'), key: 'lifecycle_state' },
  { title: t('payments.columns.invoice'), key: 'invoice_description' },
  { title: t('payments.columns.actions'), key: 'actions', sortable: false },
])

const paymentMethodItems = computed(() => [
  { title: t('subscriptions.paymentMethods.tikkie'), value: 'tikkie' },
  { title: t('subscriptions.paymentMethods.cash'), value: 'cash' },
  { title: t('subscriptions.paymentMethods.bank_transfer'), value: 'bank_transfer' },
  { title: t('subscriptions.paymentMethods.other'), value: 'other' },
])

const pendingInvoiceId = ref(null)
const rowPaymentMethod = ref('cash')

const syncBillingFromPaymentResult = data => {
  if (data?.invoice) {
    billingStore.invoices = billingStore.invoices.map(i =>
      i.id === data.invoice.id ? data.invoice : i,
    )
  }
  if (data?.subscription) {
    billingStore.current = data.subscription
    billingStore.subscriptions = billingStore.subscriptions.map(s =>
      s.id === data.subscription.id ? data.subscription : s,
    )
  }
}

const load = async () => {
  formError.value = ''
  await store.fetchClient(route.params.id)

  const c = store.current
  if (!c)
    return
  form.value = {
    name: c.name || '',
    email: c.email || '',
    date_of_birth: c.date_of_birth || '',
    gender: c.gender || '',
    phone: c.phone || '',
    address_line1: c.address_line1 || '',
    address_line2: c.address_line2 || '',
    city: c.city || '',
    postal_code: c.postal_code || '',
    country: c.country || '',
    medical_info: c.medical_info || '',
    allergies: c.allergies || '',
    injuries: c.injuries || '',
    health_history: c.health_history || '',
  }
  await Promise.all([
    billingStore.fetchForClient(route.params.id).catch(() => null),
    packagesStore.fetchForClient(route.params.id).catch(() => null),
    paymentsStore.fetchForClient(route.params.id).catch(() => null),
    goalsStore.fetchForClient(route.params.id).catch(() => null),
    plansStore.fetchForClient(route.params.id).catch(() => null),
  ])
}

onMounted(() => {
  load().catch(() => null)
  pricingPlansStore.filters.lifecycle_state = 'published'
  pricingPlansStore.filters.plan_type = ''
  pricingPlansStore.fetchPricingPlans().catch(() => null)
})

watch(() => route.params.id, () => {
  load().catch(() => null)
})

const save = async () => {
  formError.value = ''
  formSuccess.value = ''
  try {
    const { email: _email, ...payload } = form.value

    await store.updateClient(route.params.id, payload)
    formSuccess.value = t('clientDetail.saved')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
}

const review = async (decision, id) => {
  formError.value = ''
  formSuccess.value = ''
  try {
    await store.reviewChangeRequest(id, decision, reviewerNote.value)
    formSuccess.value = decision === 'approve' ? t('clientDetail.approved') : t('clientDetail.rejected')
    reviewerNote.value = ''
    await load()
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
}

const openConfirm = action => {
  const configs = {
    activate: {
      title: t('clientDetail.confirmActivateTitle'),
      body: t('clientDetail.confirmActivateBody'),
      confirmLabel: t('clientDetail.confirmActivateBtn'),
      color: 'primary',
      requireAcknowledgment: false,
    },
    pause: {
      title: t('clientDetail.confirmPauseTitle'),
      body: t('clientDetail.confirmPauseBody'),
      confirmLabel: t('clientDetail.confirmPauseBtn'),
      color: 'warning',
      requireAcknowledgment: false,
    },
    end: {
      title: t('clientDetail.confirmEndTitle'),
      body: t('clientDetail.confirmEndBody'),
      confirmLabel: t('clientDetail.confirmEndBtn'),
      color: 'error',
      requireAcknowledgment: true,
      acknowledgmentLabel: t('clientDetail.confirmEndAck'),
    },
    pauseSubscription: {
      title: t('subscriptions.confirmPauseTitle'),
      body: t('subscriptions.confirmPauseBody'),
      confirmLabel: t('subscriptions.confirmPauseBtn'),
      color: 'warning',
      requireAcknowledgment: false,
    },
    cancelSubscription: {
      title: t('subscriptions.confirmCancelTitle'),
      body: t('subscriptions.confirmCancelBody'),
      confirmLabel: t('subscriptions.confirmCancelBtn'),
      color: 'error',
      requireAcknowledgment: true,
      acknowledgmentLabel: t('subscriptions.confirmCancelAck'),
    },
    cancelSessionPackage: {
      title: t('sessionPackages.confirmCancelTitle'),
      body: t('sessionPackages.confirmCancelBody'),
      confirmLabel: t('sessionPackages.confirmCancelBtn'),
      color: 'error',
      requireAcknowledgment: true,
      acknowledgmentLabel: t('sessionPackages.confirmCancelAck'),
    },
    voidInvoice: {
      title: t('subscriptions.confirmVoidTitle'),
      body: t('subscriptions.confirmVoidBody'),
      confirmLabel: t('subscriptions.confirmVoidBtn'),
      color: 'error',
      requireAcknowledgment: true,
      acknowledgmentLabel: t('subscriptions.confirmVoidAck'),
    },
    refundPayment: {
      title: t('payments.confirmRefundTitle'),
      body: t('payments.confirmRefundBody'),
      confirmLabel: t('payments.confirmRefundBtn'),
      color: 'warning',
      requireAcknowledgment: false,
    },
  }

  const config = configs[action]
  if (!config)
    return

  confirmDialog.value = {
    open: true,
    action,
    ...config,
    acknowledgmentLabel: config.acknowledgmentLabel || '',
  }
}

const successMessageFor = action => {
  switch (action) {
  case 'activate':
    return t('clientDetail.activated')
  case 'pause':
    return t('clientDetail.paused')
  case 'resume':
    return t('clientDetail.resumed')
  case 'end':
    return t('clientDetail.endedSuccess')
  case 'pauseSubscription':
    return t('subscriptions.pausedSuccess')
  case 'cancelSubscription':
    return t('subscriptions.cancelledSuccess')
  case 'cancelSessionPackage':
    return t('sessionPackages.cancelledSuccess')
  case 'voidInvoice':
    return t('subscriptions.voidedSuccess')
  default:
    return ''
  }
}

const runTransition = async action => {
  if (!relationshipId.value)
    return

  formError.value = ''
  formSuccess.value = ''
  try {
    await store.transitionRelationship(relationshipId.value, action)
    formSuccess.value = successMessageFor(action)
    confirmDialog.value.open = false
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
}

const confirmTransition = async () => {
  const action = confirmDialog.value.action
  if (action === 'pauseSubscription' || action === 'cancelSubscription') {
    await runSubscriptionAction(action)
    
    return
  }
  if (action === 'cancelSessionPackage') {
    await cancelSessionPackage()
    
    return
  }
  if (action === 'voidInvoice') {
    await voidPendingInvoice()
    
    return
  }
  if (action === 'refundPayment') {
    await refundPendingPayment()
    
    return
  }
  await runTransition(action)
}

const resumeRelationship = async () => {
  await runTransition('resume')
}

const openStartDialog = () => {
  selectedPlanId.value = publishedSubscriptionPlans.value[0]?.id || null
  startDialog.value = true
}

const startSubscription = async () => {
  if (!relationshipId.value || !selectedPlanId.value)
    return
  formError.value = ''
  formSuccess.value = ''
  try {
    await billingStore.startSubscription({
      coachingRelationshipId: relationshipId.value,
      pricingPlanId: selectedPlanId.value,
    })
    startDialog.value = false
    formSuccess.value = t('subscriptions.startedSuccess')
    await billingStore.fetchForClient(route.params.id)
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message || billingStore.error
  }
}

const openSellPackageDialog = () => {
  selectedPackagePlanId.value = publishedPackagePlans.value[0]?.id || null
  sellPackageDialog.value = true
}

const sellSessionPackage = async () => {
  if (!relationshipId.value || !selectedPackagePlanId.value)
    return
  // F010-SEC-04: ignore double-clicks while a sell request is in flight.
  if (packagesStore.saving)
    return
  formError.value = ''
  formSuccess.value = ''
  try {
    await packagesStore.sellPackage({
      coachingRelationshipId: relationshipId.value,
      pricingPlanId: selectedPackagePlanId.value,
    })
    sellPackageDialog.value = false
    formSuccess.value = t('sessionPackages.soldSuccess')
    await Promise.all([
      packagesStore.fetchForClient(route.params.id),
      billingStore.fetchForClient(route.params.id),
    ])
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message || packagesStore.error
  }
}

const openCancelPackage = pkgId => {
  packagePendingCancelId.value = pkgId
  openConfirm('cancelSessionPackage')
}

const cancelSessionPackage = async () => {
  if (!packagePendingCancelId.value)
    return
  formError.value = ''
  formSuccess.value = ''
  try {
    await packagesStore.cancelPackage(packagePendingCancelId.value)
    formSuccess.value = t('sessionPackages.cancelledSuccess')
    confirmDialog.value.open = false
    packagePendingCancelId.value = null
    await packagesStore.fetchForClient(route.params.id)
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message || packagesStore.error
  }
}

const runSubscriptionAction = async action => {
  const sub = openSubscription.value
  if (!sub)
    return
  formError.value = ''
  formSuccess.value = ''
  try {
    if (action === 'pauseSubscription')
      await billingStore.pause(sub.id)
    else if (action === 'cancelSubscription')
      await billingStore.cancel(sub.id)
    formSuccess.value = successMessageFor(action)
    confirmDialog.value.open = false
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message || billingStore.error
  }
}

const resumeSubscription = async () => {
  const sub = openSubscription.value
  if (!sub)
    return
  formError.value = ''
  formSuccess.value = ''
  try {
    await billingStore.resume(sub.id)
    formSuccess.value = t('subscriptions.resumedSuccess')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message || billingStore.error
  }
}

const markOutstandingPaid = async () => {
  const invoice = billingStore.outstandingInvoice
  if (!invoice)
    return
  formError.value = ''
  formSuccess.value = ''
  try {
    const data = await billingStore.markInvoicePaid(invoice.id, { paymentMethod: paymentMethod.value })
    if (data?.payment)
      paymentsStore.upsertPayment(data.payment)
    formSuccess.value = t('subscriptions.markedPaidSuccess')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message || billingStore.error
  }
}

const markInvoicePaidById = async invoiceId => {
  formError.value = ''
  formSuccess.value = ''
  try {
    const data = await billingStore.markInvoicePaid(invoiceId, { paymentMethod: rowPaymentMethod.value })
    if (data?.payment)
      paymentsStore.upsertPayment(data.payment)
    formSuccess.value = t('subscriptions.markedPaidSuccess')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message || billingStore.error
  }
}

const openPartialPayment = invoice => {
  partialInvoice.value = invoice

  const remainingCents = invoice.remaining_balance_cents ?? invoice.amount_cents

  partialAmountEuros.value = (remainingCents / 100).toFixed(2).replace('.', ',')
  partialPaymentMethod.value = 'cash'
  partialDialog.value = true
}

const submitPartialPayment = async () => {
  if (!partialInvoice.value)
    return
  formError.value = ''
  formSuccess.value = ''
  try {
    const normalized = String(partialAmountEuros.value).replace(',', '.')
    const euros = Number.parseFloat(normalized)
    const amountCents = Number.isFinite(euros) ? Math.round(euros * 100) : null

    const data = await paymentsStore.recordPayment({
      invoiceId: partialInvoice.value.id,
      amountCents,
      paymentMethod: partialPaymentMethod.value,
    })

    syncBillingFromPaymentResult(data)
    partialDialog.value = false
    formSuccess.value = t('payments.partialSuccess')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message || paymentsStore.error
  }
}

const confirmPaymentById = async paymentId => {
  formError.value = ''
  formSuccess.value = ''
  try {
    const data = await paymentsStore.confirm(paymentId)

    syncBillingFromPaymentResult(data)
    formSuccess.value = t('payments.confirmedSuccess')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message || paymentsStore.error
  }
}

const rejectPaymentById = async paymentId => {
  formError.value = ''
  formSuccess.value = ''
  try {
    await paymentsStore.reject(paymentId)
    formSuccess.value = t('payments.rejectedSuccess')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message || paymentsStore.error
  }
}

const openRefundPayment = paymentId => {
  pendingPaymentId.value = paymentId
  openConfirm('refundPayment')
}

const refundPendingPayment = async () => {
  if (!pendingPaymentId.value)
    return
  formError.value = ''
  formSuccess.value = ''
  try {
    const data = await paymentsStore.refund(pendingPaymentId.value)

    syncBillingFromPaymentResult(data)
    formSuccess.value = t('payments.refundedSuccess')
    confirmDialog.value.open = false
    pendingPaymentId.value = null
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message || paymentsStore.error
  }
}

const paymentDate = payment => {
  if (payment.paid_at)
    return formatDate(payment.paid_at)

  return formatDate(payment.created_at)
}

const sendInvoiceById = async invoiceId => {
  formError.value = ''
  formSuccess.value = ''
  try {
    await billingStore.sendInvoice(invoiceId)
    formSuccess.value = t('subscriptions.sentSuccess')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message || billingStore.error
  }
}

const attachMollieById = async invoiceId => {
  formError.value = ''
  formSuccess.value = ''
  try {
    await billingStore.attachMolliePayment(invoiceId)
    formSuccess.value = t('subscriptions.mollieLinkAttached')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message || billingStore.error
  }
}

const detachMollieById = async invoiceId => {
  formError.value = ''
  formSuccess.value = ''
  try {
    await billingStore.detachMolliePayment(invoiceId)
    formSuccess.value = t('subscriptions.mollieLinkDetached')
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message || billingStore.error
  }
}

// F010-SEC-01: new collection actions require an open Coaching Relationship.
const relationshipOpenForCollection = computed(() => relationshipState.value !== 'ended')

const canAttachMollie = item => {
  return relationshipOpenForCollection.value
    && ['sent', 'overdue'].includes(item.lifecycle_state)
    && !item.mollie_checkout_url
    && (item.mollie_link_available || item.mollie_link_disabled)
}

const canDetachMollie = item => {
  return ['sent', 'overdue'].includes(item.lifecycle_state)
    && !!item.mollie_checkout_url
}

const openVoidInvoice = invoiceId => {
  pendingInvoiceId.value = invoiceId
  openConfirm('voidInvoice')
}

const voidPendingInvoice = async () => {
  if (!pendingInvoiceId.value)
    return
  formError.value = ''
  formSuccess.value = ''
  try {
    await billingStore.voidInvoice(pendingInvoiceId.value)
    formSuccess.value = t('subscriptions.voidedSuccess')
    confirmDialog.value.open = false
    pendingInvoiceId.value = null
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message || billingStore.error
  }
}

const canMarkPaid = state => ['sent', 'overdue'].includes(state)
// F010-SEC-01: Draft → Sent blocked after Ended; void/mark_paid stay available.
const canSend = state => relationshipOpenForCollection.value && state === 'draft'
const canVoid = state => ['draft', 'sent', 'overdue'].includes(state)

const invoiceAmountLabel = item => {
  const total = formatCurrency(item.amount_cents, item.currency)
  const remaining = item.remaining_balance_cents
  if (
    remaining != null
    && remaining > 0
    && remaining < item.amount_cents
    && ['sent', 'overdue'].includes(item.lifecycle_state)
  ) {
    return `${total} · ${t('payments.remaining', { amount: formatCurrency(remaining, item.currency) })}`
  }

  return total
}
</script>

<template>
  <div>
    <div class="d-flex flex-wrap align-center gap-3 mb-6">
      <VBtn
        variant="text"
        :to="{ name: 'clients' }"
        prepend-icon="ri-arrow-left-line"
      >
        {{ $t('clientDetail.backToRoster') }}
      </VBtn>
      <div v-if="store.current">
        <h1 class="text-h4 mb-1">
          {{ store.current.name || store.current.email }}
        </h1>
        <div class="d-flex flex-wrap gap-2">
          <StatusChip
            domain="client"
            :state="store.current.lifecycle_state"
            show-prefix
          />
          <StatusChip
            v-if="relationshipState"
            domain="relationship"
            :state="relationshipState"
            show-prefix
          />
          <StatusChip
            v-if="openSubscription"
            domain="subscription"
            :state="openSubscription.lifecycle_state"
            show-prefix
          />
        </div>
      </div>
    </div>

    <div
      v-if="store.loading && !store.current"
      role="status"
    >
      {{ $t('clientDetail.loading') }}
    </div>

    <VAlert
      v-else-if="store.error && !store.current"
      type="error"
      variant="tonal"
      role="alert"
    >
      {{ store.error }}
    </VAlert>

    <template v-else-if="store.current">
      <VAlert
        v-if="relationshipEnded"
        type="warning"
        variant="tonal"
        class="mb-4"
        role="status"
      >
        {{ $t('clientDetail.relationshipEnded') }}
      </VAlert>

      <VAlert
        v-if="formError"
        type="error"
        variant="tonal"
        class="mb-4"
        role="alert"
      >
        {{ formError }}
      </VAlert>
      <VAlert
        v-if="formSuccess"
        type="success"
        variant="tonal"
        class="mb-4"
        role="status"
      >
        {{ formSuccess }}
      </VAlert>

      <VTabs
        v-model="activeSection"
        class="v-tabs-pill mb-4"
        :aria-label="$t('clientDetail.workspaceTabsAria')"
      >
        <VTab value="coaching">
          {{ $t('clientDetail.tabCoaching') }}
        </VTab>
        <VTab value="billing">
          {{ $t('clientDetail.tabBilling') }}
        </VTab>
        <VTab value="profile">
          {{ $t('clientDetail.tabProfile') }}
        </VTab>
      </VTabs>

      <VWindow
        v-model="activeSection"
        class="disable-tab-transition"
        :touch="false"
      >
        <VWindowItem
          value="coaching"
          eager
        >
          <TimelinePanel
            v-if="relationshipId"
            mode="trainer"
            :coaching-relationship-id="relationshipId"
          />

          <GoalsPanel
            v-if="relationshipId"
            mode="trainer"
            :coaching-relationship-id="relationshipId"
            :can-mutate="!relationshipEnded"
          />

          <PlansPanel
            v-if="relationshipId"
            mode="trainer"
            :coaching-relationship-id="relationshipId"
            :can-mutate="!relationshipEnded"
          />

          <ProgressPanel
            v-if="relationshipId"
            mode="trainer"
            :client-id="route.params.id"
            :coaching-relationship-id="relationshipId"
            :relationship-state="relationshipState"
            :can-mutate="!relationshipEnded"
          />

          <NotesPanel
            v-if="relationshipId"
            mode="trainer"
            :client-id="route.params.id"
            :coaching-relationship-id="relationshipId"
            :can-mutate="!relationshipEnded"
          />

          <ObservationsPanel
            v-if="relationshipId"
            mode="trainer"
            :client-id="route.params.id"
            :coaching-relationship-id="relationshipId"
            :can-mutate="!relationshipEnded"
          />

          <MilestonesPanel
            v-if="relationshipId"
            mode="trainer"
            :coaching-relationship-id="relationshipId"
            :can-mutate="!relationshipEnded"
          />
        </VWindowItem>

        <VWindowItem
          value="billing"
          eager
        >
          <VCard
            v-if="relationshipState"
            flat
            border
            class="mb-4"
          >
            <VCardItem>
              <VCardTitle>{{ $t('clientDetail.relationshipTitle') }}</VCardTitle>
            </VCardItem>
            <VCardText>
              <div class="d-flex flex-wrap gap-2">
                <VBtn
                  v-if="relationshipState === 'prospect'"
                  color="primary"
                  :loading="store.saving"
                  @click="openConfirm('activate')"
                >
                  {{ $t('clientDetail.activate') }}
                </VBtn>
                <VBtn
                  v-if="relationshipState === 'active'"
                  color="warning"
                  variant="tonal"
                  :loading="store.saving"
                  @click="openConfirm('pause')"
                >
                  {{ $t('clientDetail.pause') }}
                </VBtn>
                <VBtn
                  v-if="relationshipState === 'paused'"
                  color="primary"
                  :loading="store.saving"
                  @click="resumeRelationship"
                >
                  {{ $t('clientDetail.resume') }}
                </VBtn>
                <VBtn
                  v-if="relationshipState !== 'ended'"
                  color="error"
                  variant="tonal"
                  :loading="store.saving"
                  @click="openConfirm('end')"
                >
                  {{ $t('clientDetail.end') }}
                </VBtn>
              </div>
            </VCardText>
          </VCard>

          <VCard
            v-if="relationshipState && relationshipState !== 'ended'"
            flat
            border
            class="mb-4"
          >
            <VCardItem>
              <VCardTitle>{{ $t('subscriptions.billingTitle') }}</VCardTitle>
              <VCardSubtitle>{{ $t('subscriptions.billingSubtitle') }}</VCardSubtitle>
            </VCardItem>
            <VCardText>
              <div
                v-if="billingStore.loading && !openSubscription"
                role="status"
                class="text-medium-emphasis mb-4"
              >
                {{ $t('subscriptions.loading') }}
              </div>

              <VAlert
                v-else-if="billingStore.error && !openSubscription"
                type="error"
                variant="tonal"
                class="mb-4"
                role="alert"
              >
                {{ billingStore.error }}
              </VAlert>

              <template v-else-if="!openSubscription">
                <p class="text-body-2 text-medium-emphasis mb-4">
                  {{ $t('subscriptions.emptyClient') }}
                </p>
                <VBtn
                  color="primary"
                  :disabled="!['active', 'paused'].includes(relationshipState)"
                  @click="openStartDialog"
                >
                  {{ $t('subscriptions.start') }}
                </VBtn>
              </template>

              <template v-else>
                <div class="d-flex flex-wrap align-center gap-3 mb-4">
                  <StatusChip
                    domain="subscription"
                    :state="openSubscription.lifecycle_state"
                  />
                  <span class="text-body-1">
                    {{ openSubscription.plan_name }}
                    · {{ formatCurrency(openSubscription.price_cents, openSubscription.currency) }}
                    · {{ billingCadenceLabel(openSubscription.billing_cadence) }}
                  </span>
                </div>
                <p class="text-body-2 text-medium-emphasis mb-4">
                  {{ $t('subscriptions.nextBilling', { date: openSubscription.next_billing_on }) }}
                </p>

                <VAlert
                  v-if="openSubscription.lifecycle_state === 'past_due'"
                  type="warning"
                  variant="tonal"
                  class="mb-4"
                  role="status"
                >
                  <div class="d-flex flex-wrap align-center gap-3">
                    <span>{{ $t('subscriptions.pastDueNotice') }}</span>
                    <VSelect
                      v-model="paymentMethod"
                      density="compact"
                      hide-details
                      style="max-inline-size: 12rem;"
                      :items="[
                        { title: $t('subscriptions.paymentMethods.cash'), value: 'cash' },
                        { title: $t('subscriptions.paymentMethods.bank_transfer'), value: 'bank_transfer' },
                        { title: $t('subscriptions.paymentMethods.tikkie'), value: 'tikkie' },
                        { title: $t('subscriptions.paymentMethods.other'), value: 'other' },
                      ]"
                      :label="$t('subscriptions.paymentMethod')"
                    />
                    <VBtn
                      color="primary"
                      :loading="billingStore.saving"
                      :disabled="!billingStore.outstandingInvoice"
                      @click="markOutstandingPaid"
                    >
                      {{ $t('subscriptions.markPaid') }}
                    </VBtn>
                  </div>
                </VAlert>

                <div class="d-flex flex-wrap gap-2 mb-6">
                  <VBtn
                    v-if="['active', 'past_due'].includes(openSubscription.lifecycle_state)"
                    color="warning"
                    variant="tonal"
                    :loading="billingStore.saving"
                    @click="openConfirm('pauseSubscription')"
                  >
                    {{ $t('subscriptions.pause') }}
                  </VBtn>
                  <VBtn
                    v-if="openSubscription.lifecycle_state === 'paused'"
                    color="primary"
                    :loading="billingStore.saving"
                    @click="resumeSubscription"
                  >
                    {{ $t('subscriptions.resume') }}
                  </VBtn>
                  <VBtn
                    color="error"
                    variant="tonal"
                    :loading="billingStore.saving"
                    @click="openConfirm('cancelSubscription')"
                  >
                    {{ $t('subscriptions.cancel') }}
                  </VBtn>
                </div>

                <h3 class="text-h6 mb-2">
                  {{ $t('subscriptions.invoicesTitle') }}
                </h3>
                <VAlert
                  v-if="!billingStore.invoices.length"
                  type="info"
                  variant="tonal"
                  role="status"
                >
                  {{ $t('subscriptions.invoicesEmpty') }}
                </VAlert>
                <VDataTable
                  v-else
                  :headers="invoiceHeaders"
                  :items="billingStore.sortedInvoices"
                  :items-per-page="5"
                  density="comfortable"
                  class="elevation-0"
                >
                  <template #item.amount="{ item }">
                    {{ invoiceAmountLabel(item) }}
                  </template>
                  <template #item.due_on="{ item }">
                    {{ formatDate(item.due_on) }}
                  </template>
                  <template #item.lifecycle_state="{ item }">
                    <StatusChip
                      domain="invoice"
                      :state="item.lifecycle_state"
                    />
                    <div
                      v-if="item.mollie_checkout_url"
                      class="text-caption text-medium-emphasis mt-1"
                    >
                      {{ $t('subscriptions.mollieLinkActive') }}
                    </div>
                  </template>
                  <template #item.actions="{ item }">
                    <div class="d-flex flex-wrap ga-1 justify-end">
                      <VBtn
                        v-if="canMarkPaid(item.lifecycle_state)"
                        size="small"
                        color="primary"
                        variant="tonal"
                        :loading="billingStore.saving"
                        :aria-label="$t('subscriptions.markPaid')"
                        @click="markInvoicePaidById(item.id)"
                      >
                        {{ $t('subscriptions.markPaid') }}
                      </VBtn>
                      <VBtn
                        v-if="canMarkPaid(item.lifecycle_state)"
                        size="small"
                        color="primary"
                        variant="text"
                        :loading="paymentsStore.saving"
                        :aria-label="$t('payments.recordPartial')"
                        @click="openPartialPayment(item)"
                      >
                        {{ $t('payments.recordPartial') }}
                      </VBtn>
                      <VBtn
                        v-if="canAttachMollie(item)"
                        size="small"
                        color="primary"
                        variant="outlined"
                        :loading="billingStore.saving"
                        :aria-label="$t('subscriptions.attachMollie')"
                        @click="attachMollieById(item.id)"
                      >
                        {{ $t('subscriptions.attachMollie') }}
                      </VBtn>
                      <VBtn
                        v-if="canDetachMollie(item)"
                        size="small"
                        color="secondary"
                        variant="outlined"
                        :loading="billingStore.saving"
                        :aria-label="$t('subscriptions.detachMollie')"
                        @click="detachMollieById(item.id)"
                      >
                        {{ $t('subscriptions.detachMollie') }}
                      </VBtn>
                      <VBtn
                        v-if="canSend(item.lifecycle_state)"
                        size="small"
                        color="primary"
                        variant="tonal"
                        :loading="billingStore.saving"
                        :aria-label="$t('subscriptions.sendInvoice')"
                        @click="sendInvoiceById(item.id)"
                      >
                        {{ $t('subscriptions.sendInvoice') }}
                      </VBtn>
                      <VBtn
                        v-if="canVoid(item.lifecycle_state)"
                        size="small"
                        color="error"
                        variant="tonal"
                        :loading="billingStore.saving"
                        :aria-label="$t('subscriptions.voidInvoice')"
                        @click="openVoidInvoice(item.id)"
                      >
                        {{ $t('subscriptions.voidInvoice') }}
                      </VBtn>
                    </div>
                  </template>
                </VDataTable>
              </template>

              <template v-if="!openSubscription && billingStore.invoices.length">
                <h3 class="text-h6 mb-2 mt-6">
                  {{ $t('subscriptions.invoicesTitle') }}
                </h3>
                <VDataTable
                  :headers="invoiceHeaders"
                  :items="billingStore.sortedInvoices"
                  :items-per-page="5"
                  density="comfortable"
                  class="elevation-0"
                >
                  <template #item.amount="{ item }">
                    {{ invoiceAmountLabel(item) }}
                  </template>
                  <template #item.due_on="{ item }">
                    {{ formatDate(item.due_on) }}
                  </template>
                  <template #item.lifecycle_state="{ item }">
                    <StatusChip
                      domain="invoice"
                      :state="item.lifecycle_state"
                    />
                    <div
                      v-if="item.mollie_checkout_url"
                      class="text-caption text-medium-emphasis mt-1"
                    >
                      {{ $t('subscriptions.mollieLinkActive') }}
                    </div>
                  </template>
                  <template #item.actions="{ item }">
                    <div class="d-flex flex-wrap ga-1 justify-end">
                      <VBtn
                        v-if="canMarkPaid(item.lifecycle_state)"
                        size="small"
                        color="primary"
                        variant="tonal"
                        :loading="billingStore.saving"
                        :aria-label="$t('subscriptions.markPaid')"
                        @click="markInvoicePaidById(item.id)"
                      >
                        {{ $t('subscriptions.markPaid') }}
                      </VBtn>
                      <VBtn
                        v-if="canMarkPaid(item.lifecycle_state)"
                        size="small"
                        color="primary"
                        variant="text"
                        :loading="paymentsStore.saving"
                        :aria-label="$t('payments.recordPartial')"
                        @click="openPartialPayment(item)"
                      >
                        {{ $t('payments.recordPartial') }}
                      </VBtn>
                      <VBtn
                        v-if="canAttachMollie(item)"
                        size="small"
                        color="primary"
                        variant="outlined"
                        :loading="billingStore.saving"
                        :aria-label="$t('subscriptions.attachMollie')"
                        @click="attachMollieById(item.id)"
                      >
                        {{ $t('subscriptions.attachMollie') }}
                      </VBtn>
                      <VBtn
                        v-if="canDetachMollie(item)"
                        size="small"
                        color="secondary"
                        variant="outlined"
                        :loading="billingStore.saving"
                        :aria-label="$t('subscriptions.detachMollie')"
                        @click="detachMollieById(item.id)"
                      >
                        {{ $t('subscriptions.detachMollie') }}
                      </VBtn>
                      <VBtn
                        v-if="canSend(item.lifecycle_state)"
                        size="small"
                        color="primary"
                        variant="tonal"
                        :loading="billingStore.saving"
                        :aria-label="$t('subscriptions.sendInvoice')"
                        @click="sendInvoiceById(item.id)"
                      >
                        {{ $t('subscriptions.sendInvoice') }}
                      </VBtn>
                      <VBtn
                        v-if="canVoid(item.lifecycle_state)"
                        size="small"
                        color="error"
                        variant="tonal"
                        :loading="billingStore.saving"
                        :aria-label="$t('subscriptions.voidInvoice')"
                        @click="openVoidInvoice(item.id)"
                      >
                        {{ $t('subscriptions.voidInvoice') }}
                      </VBtn>
                    </div>
                  </template>
                </VDataTable>
              </template>
            </VCardText>
          </VCard>

          <VCard
            v-if="relationshipState && relationshipState !== 'ended'"
            flat
            border
            class="mb-4"
          >
            <VCardItem>
              <VCardTitle>{{ $t('payments.title') }}</VCardTitle>
              <VCardSubtitle>{{ $t('payments.subtitle') }}</VCardSubtitle>
            </VCardItem>
            <VCardText>
              <div
                v-if="paymentsStore.loading && paymentsStore.isEmpty"
                role="status"
                class="text-medium-emphasis mb-4"
              >
                {{ $t('payments.loading') }}
              </div>
              <VAlert
                v-else-if="paymentsStore.error && paymentsStore.isEmpty"
                type="error"
                variant="tonal"
                class="mb-4"
                role="alert"
              >
                {{ paymentsStore.error }}
              </VAlert>
              <VAlert
                v-else-if="paymentsStore.isEmpty"
                type="info"
                variant="tonal"
                role="status"
              >
                {{ $t('payments.empty') }}
              </VAlert>
              <VDataTable
                v-else
                :headers="paymentHeaders"
                :items="paymentsStore.payments"
                :items-per-page="8"
                density="comfortable"
                class="elevation-0"
              >
                <template #item.date="{ item }">
                  {{ paymentDate(item) }}
                </template>
                <template #item.amount="{ item }">
                  {{ formatCurrency(item.amount_cents, item.currency) }}
                </template>
                <template #item.payment_method="{ item }">
                  {{ paymentMethodLabel(item.payment_method) }}
                </template>
                <template #item.lifecycle_state="{ item }">
                  <StatusChip
                    domain="payment"
                    :state="item.lifecycle_state"
                  />
                </template>
                <template #item.actions="{ item }">
                  <div class="d-flex flex-wrap ga-1 justify-end">
                    <VBtn
                      v-if="item.lifecycle_state === 'pending'"
                      size="small"
                      color="primary"
                      variant="tonal"
                      :loading="paymentsStore.saving"
                      :aria-label="$t('payments.confirm')"
                      @click="confirmPaymentById(item.id)"
                    >
                      {{ $t('payments.confirm') }}
                    </VBtn>
                    <VBtn
                      v-if="item.lifecycle_state === 'pending'"
                      size="small"
                      color="error"
                      variant="tonal"
                      :loading="paymentsStore.saving"
                      :aria-label="$t('payments.reject')"
                      @click="rejectPaymentById(item.id)"
                    >
                      {{ $t('payments.reject') }}
                    </VBtn>
                    <VBtn
                      v-if="item.lifecycle_state === 'paid'"
                      size="small"
                      color="warning"
                      variant="tonal"
                      :loading="paymentsStore.saving"
                      :aria-label="$t('payments.refund')"
                      @click="openRefundPayment(item.id)"
                    >
                      {{ $t('payments.refund') }}
                    </VBtn>
                  </div>
                </template>
              </VDataTable>
            </VCardText>
          </VCard>

          <VCard
            v-if="relationshipState && relationshipState !== 'ended'"
            flat
            border
            class="mb-4"
          >
            <VCardItem>
              <VCardTitle>{{ $t('sessionPackages.title') }}</VCardTitle>
              <VCardSubtitle>{{ $t('sessionPackages.subtitle') }}</VCardSubtitle>
            </VCardItem>
            <VCardText>
              <div
                v-if="packagesStore.loading && packagesStore.isEmpty"
                role="status"
                class="text-medium-emphasis mb-4"
              >
                {{ $t('sessionPackages.loading') }}
              </div>

              <VAlert
                v-else-if="packagesStore.error && packagesStore.isEmpty"
                type="error"
                variant="tonal"
                class="mb-4"
                role="alert"
              >
                {{ packagesStore.error }}
              </VAlert>

              <template v-else-if="packagesStore.isEmpty">
                <p class="text-body-2 text-medium-emphasis mb-4">
                  {{ $t('sessionPackages.emptyClient') }}
                </p>
                <VBtn
                  color="primary"
                  :disabled="!['active', 'paused'].includes(relationshipState)"
                  @click="openSellPackageDialog"
                >
                  {{ $t('sessionPackages.sell') }}
                </VBtn>
              </template>

              <template v-else>
                <VAlert
                  v-for="pkg in packagesStore.packages.filter(p => p.nearing_depletion || p.nearing_expiry)"
                  :key="`warn-${pkg.id}`"
                  type="warning"
                  variant="tonal"
                  class="mb-3"
                  role="status"
                >
                  <span v-if="pkg.nearing_depletion">
                    {{ $t('sessionPackages.nearingDepletion', { count: pkg.sessions_remaining }) }}
                  </span>
                  <span v-if="pkg.nearing_expiry">
                    {{ $t('sessionPackages.nearingExpiry', { date: formatDate(pkg.expires_on) }) }}
                  </span>
                </VAlert>

                <VList
                  lines="two"
                  class="bg-transparent mb-4"
                >
                  <VListItem
                    v-for="pkg in packagesStore.packages"
                    :key="pkg.id"
                  >
                    <VListItemTitle>
                      {{ pkg.plan_name }}
                      ·
                      {{ $t('sessionPackages.remaining', { count: pkg.sessions_remaining }) }}
                    </VListItemTitle>
                    <VListItemSubtitle>
                      {{ $t('sessionPackages.expiresOn', { date: formatDate(pkg.expires_on) }) }}
                      · {{ formatCurrency(pkg.price_cents, pkg.currency) }}
                    </VListItemSubtitle>
                    <template #append>
                      <div class="d-flex align-center gap-2">
                        <StatusChip
                          domain="session_package"
                          :state="pkg.lifecycle_state"
                        />
                        <VBtn
                          v-if="pkg.lifecycle_state === 'active'"
                          size="small"
                          color="error"
                          variant="tonal"
                          :loading="packagesStore.saving"
                          @click="openCancelPackage(pkg.id)"
                        >
                          {{ $t('sessionPackages.cancel') }}
                        </VBtn>
                      </div>
                    </template>
                  </VListItem>
                </VList>

                <VBtn
                  color="primary"
                  variant="tonal"
                  :disabled="!['active', 'paused'].includes(relationshipState)"
                  @click="openSellPackageDialog"
                >
                  {{ $t('sessionPackages.sell') }}
                </VBtn>
              </template>
            </VCardText>
          </VCard>

          <VDialog
            v-model="partialDialog"
            max-width="480"
          >
            <VCard :title="$t('payments.partialDialogTitle')">
              <VCardText>
                <p class="text-body-2 mb-4">
                  {{ $t('payments.partialDialogBody') }}
                </p>
                <VTextField
                  v-model="partialAmountEuros"
                  :label="$t('payments.amount')"
                  :hint="$t('payments.amountHint')"
                  persistent-hint
                  class="mb-4"
                  inputmode="decimal"
                />
                <VSelect
                  v-model="partialPaymentMethod"
                  :items="paymentMethodItems"
                  :label="$t('subscriptions.paymentMethod')"
                />
              </VCardText>
              <VCardActions>
                <VSpacer />
                <VBtn
                  variant="text"
                  @click="partialDialog = false"
                >
                  {{ $t('payments.cancel') }}
                </VBtn>
                <VBtn
                  color="primary"
                  :loading="paymentsStore.saving"
                  @click="submitPartialPayment"
                >
                  {{ $t('payments.save') }}
                </VBtn>
              </VCardActions>
            </VCard>
          </VDialog>

          <VDialog
            v-model="sellPackageDialog"
            max-width="520"
          >
            <VCard :title="$t('sessionPackages.sellDialogTitle')">
              <VCardText>
                <p class="text-body-2 mb-4">
                  {{ $t('sessionPackages.sellDialogBody') }}
                </p>
                <VAlert
                  v-if="!publishedPackagePlans.length"
                  type="info"
                  variant="tonal"
                  role="status"
                >
                  {{ $t('sessionPackages.noPublishedPlans') }}
                </VAlert>
                <template v-else>
                  <VSelect
                    v-model="selectedPackagePlanId"
                    :items="publishedPackagePlans.map(p => ({
                      title: `${p.name} — ${p.session_count} sessies / ${p.validity_days} dagen — ${formatCurrency(p.price_cents, p.currency)}`,
                      value: p.id,
                    }))"
                    :label="$t('sessionPackages.pickPlan')"
                    class="mb-4"
                  />
                  <VAlert
                    v-if="selectedPackagePlan"
                    type="info"
                    variant="tonal"
                    role="status"
                  >
                    {{ $t('sessionPackages.confirmTerms', {
                      count: selectedPackagePlan.session_count,
                      days: selectedPackagePlan.validity_days,
                      price: formatCurrency(selectedPackagePlan.price_cents, selectedPackagePlan.currency),
                    }) }}
                  </VAlert>
                </template>
              </VCardText>
              <VCardActions>
                <VSpacer />
                <VBtn
                  variant="text"
                  @click="sellPackageDialog = false"
                >
                  {{ $t('common.cancel') }}
                </VBtn>
                <VBtn
                  color="primary"
                  :loading="packagesStore.saving"
                  :disabled="!selectedPackagePlanId || packagesStore.saving"
                  @click="sellSessionPackage"
                >
                  {{ $t('sessionPackages.confirmSell') }}
                </VBtn>
              </VCardActions>
            </VCard>
          </VDialog>

          <VDialog
            v-model="startDialog"
            max-width="520"
          >
            <VCard :title="$t('subscriptions.startDialogTitle')">
              <VCardText>
                <p class="text-body-2 mb-4">
                  {{ $t('subscriptions.startDialogBody') }}
                </p>
                <VAlert
                  v-if="!publishedSubscriptionPlans.length"
                  type="info"
                  variant="tonal"
                  role="status"
                >
                  {{ $t('subscriptions.noPublishedPlans') }}
                </VAlert>
                <template v-else>
                  <VSelect
                    v-model="selectedPlanId"
                    :items="publishedSubscriptionPlans.map(p => ({
                      title: `${p.name} — ${formatCurrency(p.price_cents, p.currency)} (${billingCadenceLabel(p.billing_cadence)})`,
                      value: p.id,
                    }))"
                    :label="$t('subscriptions.pickPlan')"
                    class="mb-4"
                  />
                  <VAlert
                    v-if="selectedPlan"
                    type="info"
                    variant="tonal"
                    role="status"
                  >
                    {{ $t('subscriptions.confirmTerms', {
                      cadence: billingCadenceLabel(selectedPlan.billing_cadence),
                      price: formatCurrency(selectedPlan.price_cents, selectedPlan.currency),
                    }) }}
                  </VAlert>
                </template>
              </VCardText>
              <VCardActions>
                <VSpacer />
                <VBtn
                  variant="text"
                  @click="startDialog = false"
                >
                  {{ $t('common.cancel') }}
                </VBtn>
                <VBtn
                  color="primary"
                  :loading="billingStore.saving"
                  :disabled="!selectedPlanId"
                  @click="startSubscription"
                >
                  {{ $t('subscriptions.confirmStart') }}
                </VBtn>
              </VCardActions>
            </VCard>
          </VDialog>
        </VWindowItem>

        <VWindowItem
          value="profile"
          eager
        >
          <VForm @submit.prevent="save">
            <VCard
              flat
              border
              class="mb-4"
            >
              <VCardItem>
                <VCardTitle>{{ $t('clientDetail.personalInfoTitle') }}</VCardTitle>
              </VCardItem>
              <VCardText>
                <VRow>
                  <VCol
                    cols="12"
                    md="6"
                  >
                    <VTextField
                      v-model="form.name"
                      :label="$t('common.name')"
                      :disabled="relationshipEnded || store.saving"
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    md="6"
                  >
                    <VTextField
                      :model-value="form.email"
                      :label="$t('common.email')"
                      disabled
                      :hint="$t('clientDetail.emailHint')"
                      persistent-hint
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    md="6"
                  >
                    <VTextField
                      v-model="form.date_of_birth"
                      :label="$t('clientDetail.dateOfBirthLabel')"
                      type="date"
                      :disabled="relationshipEnded || store.saving"
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    md="6"
                  >
                    <VTextField
                      v-model="form.gender"
                      :label="$t('clientDetail.genderLabel')"
                      :disabled="relationshipEnded || store.saving"
                    />
                  </VCol>
                </VRow>
              </VCardText>
            </VCard>

            <VCard
              flat
              border
              class="mb-4"
            >
              <VCardItem>
                <VCardTitle>{{ $t('clientDetail.contactInfoTitle') }}</VCardTitle>
              </VCardItem>
              <VCardText>
                <VRow>
                  <VCol
                    cols="12"
                    md="6"
                  >
                    <VTextField
                      v-model="form.phone"
                      :label="$t('common.phone')"
                      :disabled="relationshipEnded || store.saving"
                    />
                  </VCol>
                  <VCol cols="12">
                    <VTextField
                      v-model="form.address_line1"
                      :label="$t('common.addressLine1')"
                      :disabled="relationshipEnded || store.saving"
                    />
                  </VCol>
                  <VCol cols="12">
                    <VTextField
                      v-model="form.address_line2"
                      :label="$t('common.addressLine2')"
                      :disabled="relationshipEnded || store.saving"
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    md="4"
                  >
                    <VTextField
                      v-model="form.city"
                      :label="$t('common.city')"
                      :disabled="relationshipEnded || store.saving"
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    md="4"
                  >
                    <VTextField
                      v-model="form.postal_code"
                      :label="$t('common.postalCode')"
                      :disabled="relationshipEnded || store.saving"
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    md="4"
                  >
                    <VTextField
                      v-model="form.country"
                      :label="$t('common.country')"
                      :disabled="relationshipEnded || store.saving"
                    />
                  </VCol>
                </VRow>
              </VCardText>
            </VCard>

            <VCard
              flat
              border
              class="mb-4 health-section"
            >
              <VCardItem>
                <VCardTitle>{{ $t('clientDetail.healthProfileTitle') }}</VCardTitle>
                <VCardSubtitle>
                  {{ $t('clientDetail.healthProfileSubtitle') }}
                </VCardSubtitle>
              </VCardItem>
              <VCardText>
                <VRow>
                  <VCol cols="12">
                    <VTextarea
                      v-model="form.medical_info"
                      :label="$t('clientDetail.medicalInfoLabel')"
                      rows="2"
                      :disabled="relationshipEnded || store.saving"
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    md="6"
                  >
                    <VTextarea
                      v-model="form.allergies"
                      :label="$t('clientDetail.allergiesLabel')"
                      rows="2"
                      :disabled="relationshipEnded || store.saving"
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    md="6"
                  >
                    <VTextarea
                      v-model="form.injuries"
                      :label="$t('clientDetail.injuriesLabel')"
                      rows="2"
                      :disabled="relationshipEnded || store.saving"
                    />
                  </VCol>
                  <VCol cols="12">
                    <VTextarea
                      v-model="form.health_history"
                      :label="$t('clientDetail.healthHistoryLabel')"
                      rows="3"
                      :disabled="relationshipEnded || store.saving"
                    />
                  </VCol>
                </VRow>
              </VCardText>
            </VCard>

            <VBtn
              type="submit"
              color="primary"
              :loading="store.saving"
              :disabled="relationshipEnded"
              class="mb-8"
            >
              {{ $t('clientDetail.saveProfile') }}
            </VBtn>
          </VForm>

          <VCard
            v-if="store.pendingChangeRequests.length"
            flat
            border
            class="mb-4"
          >
            <VCardItem>
              <VCardTitle>{{ $t('clientDetail.changeRequestsTitle') }}</VCardTitle>
              <VCardSubtitle>{{ $t('clientDetail.changeRequestsSubtitle') }}</VCardSubtitle>
            </VCardItem>
            <VCardText
              v-for="req in store.pendingChangeRequests"
              :key="req.id"
            >
              <p class="text-body-2">
                <strong>{{ $t('clientDetail.proposedInjuries') }}</strong> {{ req.proposed_injuries || '—' }}
              </p>
              <p class="text-body-2">
                <strong>{{ $t('clientDetail.proposedAllergies') }}</strong> {{ req.proposed_allergies || '—' }}
              </p>
              <p class="text-body-2">
                <strong>{{ $t('clientDetail.proposedMedicalInfo') }}</strong> {{ req.proposed_medical_info || '—' }}
              </p>
              <p class="text-body-2 mb-3">
                <strong>{{ $t('clientDetail.proposedHealthHistory') }}</strong> {{ req.proposed_health_history || '—' }}
              </p>
              <VTextField
                v-model="reviewerNote"
                :label="$t('clientDetail.reviewerNoteLabel')"
                class="mb-3"
              />
              <div class="d-flex gap-2">
                <VBtn
                  color="primary"
                  :loading="store.saving"
                  @click="review('approve', req.id)"
                >
                  {{ $t('common.approve') }}
                </VBtn>
                <VBtn
                  variant="tonal"
                  color="error"
                  :loading="store.saving"
                  @click="review('reject', req.id)"
                >
                  {{ $t('common.reject') }}
                </VBtn>
              </div>
            </VCardText>
          </VCard>
        </VWindowItem>
      </VWindow>
    </template>

    <ConfirmDialog
      v-model="confirmDialog.open"
      :title="confirmDialog.title"
      :body="confirmDialog.body"
      :confirm-label="confirmDialog.confirmLabel"
      :color="confirmDialog.color"
      :require-acknowledgment="confirmDialog.requireAcknowledgment"
      :acknowledgment-label="confirmDialog.acknowledgmentLabel"
      :loading="store.saving || billingStore.saving || paymentsStore.saving"
      @confirm="confirmTransition"
    />
  </div>
</template>

<style scoped>
.health-section {
  border-inline-start: 4px solid rgb(var(--v-theme-warning));
}
</style>
